<template>
    <div class="dash">
        <div class="demo-banner">
            <span>{{ $t('dash.demo.banner.text') }}</span>
            <div class="demo-banner__actions">
                <router-link :to="{ name: 'auth' }" class="demo-banner__link">{{ $t('dash.demo.banner.login') }}</router-link>
                <router-link :to="{ name: 'auth', query: { ref: 'demo' } }" class="demo-banner__link demo-banner__link--primary">{{ $t('dash.demo.banner.signup') }}</router-link>
            </div>
        </div>

        <!-- Same portal targets/overlay singletons Dash.vue registers for every /dash/* page -
             PostCreator's own children (mention/music/product pickers, tooltips, page picker,
             assign-user modal, ...) teleport into these via <portal to="dash">/<portal to=
             "media-editor-dash"> or render through these singletons directly. Without them here,
             those overlays silently have nowhere to mount. -->
        <portal-target name="dash" multiple/>
        <portal-target name="media-editor-dash" multiple/>
        <componentTootip/>
        <componentContextMenu/>
        <componentModal/>
        <selectUsers/>
        <tableOverlay/>

        <div class="dash-section">
            <div class="main-wrapper">
                <SelectPageSection title-key="dash.select_page_section.publications">
                    <template #between-search-and-list>
                        <PersonalizePages v-if="ready" @personalized="refreshDemoData" />
                    </template>
                </SelectPageSection>
                <div class="main-content">
                    <div class="section large postCreator">
                        <PostCreator v-if="ready" mode="create" />
                        <div v-else class="demo-post-creator__loading">
                            <div ref="turnstileContainer"></div>
                            <p>{{ $t('dash.demo.loading') }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { EventBus } from '@/event-bus'
import PostCreator from './PostCreator.vue'
import SelectPageSection from '../SelectPageSection.vue'
import componentTootip from '../tooltip/tooltip.vue'
import componentContextMenu from '../contextMenu/contextMenu.vue'
import componentModal from '../modalPopup.vue'
import selectUsers from '../overlay/SelectUsers.vue'
import tableOverlay from '../overlay/Table.vue'
import PersonalizePages from '../demoPostCreator/PersonalizePages.vue'
import { buildDemoData, loadCustomBrands } from '../demoPostCreator/fakeData.js'

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

// Shared across every mount of this component (Turnstile's own script is a singleton anyway) -
// avoids injecting the <script> tag more than once if the visitor navigates back to the demo.
function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve()
    if (window.__turnstileScriptPromise) return window.__turnstileScriptPromise

    window.__turnstileScriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script')
        script.src = TURNSTILE_SCRIPT_URL
        script.async = true
        script.defer = true
        script.onload = resolve
        script.onerror = reject
        document.head.appendChild(script)
    })

    return window.__turnstileScriptPromise
}

export default {
    name: 'DemoPostCreator',

    components: {
        PostCreator,
        SelectPageSection,
        PersonalizePages,
        componentTootip,
        componentContextMenu,
        componentModal,
        selectUsers,
        tableOverlay
    },

    data() {
        return {
            ready: false
        }
    },

    created() {
        this.$store.commit('demo/setActive', true)
        this.refreshDemoData()
    },

    async mounted() {
        const sitekey = process.env.VUE_APP_TURNSTILE_SITEKEY

        if (!sitekey) {
            // No sitekey configured (e.g. local dev without Cloudflare set up) - go straight to
            // session creation; the backend itself no-ops the Turnstile check when its own
            // demo.turnstile_secret is unset, so this stays consistent either way.
            return this.createSession(null)
        }

        try {
            await loadTurnstileScript()
            window.turnstile.render(this.$refs.turnstileContainer, {
                sitekey,
                callback: (token) => this.createSession(token),
                'error-callback': () => {
                    console.error('[DemoPostCreator] Turnstile challenge failed')
                }
            })
        } catch (error) {
            console.error('[DemoPostCreator] failed to load Turnstile', error)
            this.createSession(null)
        }
    },

    methods: {
        refreshDemoData() {
            // Fictional pages/groups (or, once personalized, one group per brand the visitor has
            // added - see fakeData.js) - the select bar (SelectPageSection/SelectPage) reads
            // these store getters unmodified, no component change needed.
            const { pages, groups, variables, variableValues } = buildDemoData(loadCustomBrands())
            this.$store.commit('selectPage/updateAllPages', pages)
            this.$store.commit('selectPage/updateSelectPageGroups', groups)
            this.$store.commit('selectPage/updateIsInit', true)

            // Fake custom variables (nom/adresse/lien) so the "variable" picker has something to
            // show - fetchVariables (store/modules/variables.js) skips the real endpoint in demo.
            this.$store.commit('variables/updateVariables', variables)
            this.$store.commit('variables/updateValues', variableValues)
            this.$store.commit('variables/updateIsInit', true)

            // The page roster just changed (brand added/removed) - reset the selection so
            // SelectPageSection's own watcher auto-selects fresh from the new roster.
            this.$store.commit('selectPage/updatePagesSelected', { saveToLocalStorage: false, pagesId: [] })
        },

        async createSession(turnstileToken) {
            if (this.ready) return // Turnstile can call back more than once (e.g. after expiry+retry)

            try {
                const { data } = await axios.post(`${process.env.VUE_APP_API_HOST}/demo/session`, { turnstileToken })
                this.$store.commit('demo/setSession', { token: data.token, networks: data.networks })
            } catch (error) {
                console.error('[DemoPostCreator] failed to create demo session', error)
            }

            this.ready = true
            // Only greet a first-time visitor - if a brand is already personalized (this browser
            // used the demo before, or came back to it), the "personalize?" pitch is pointless.
            if (!loadCustomBrands().length) this.showWelcomePopup()
        },

        showWelcomePopup() {
            EventBus.$emit(
                'showModal',
                this.$t('dash.demo.welcome-popup.title'),
                this.$t('dash.demo.welcome-popup.subtitle'),
                this.$t('dash.demo.welcome-popup.personalize'),
                this.$t('dash.demo.welcome-popup.skip'),
                () => EventBus.$emit('demo/openPersonalize'),
                () => {}
            )
        }
    }
}
</script>

<style lang="scss" scoped>
.demo-post-creator__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    height: calc(100vh - 40px); // 40px = .demo-banner's height
}

.demo-banner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    height: 40px;
    background-color: #5e254a;
    color: #fff;
    font-size: 12.8px;
    font-weight: 600;

    &__actions {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__link {
        color: #fff;
        text-decoration: underline;
        cursor: pointer;

        &--primary {
            // Same shape as the app's .btn-secondary CTA (utils/button.scss) - 5.6px, not a pill.
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 12px;
            height: 24px;
            border-radius: 5.6px;
            background-color: #B64985;
            text-decoration: none;
        }
    }
}
</style>
